import React, { useEffect, useState } from "react";
import Layout from "../../components/Sidebar";
import api from "../../api";
import { toast } from "sonner";
import { exportToExcel } from "../../utils/exportExcel";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "../../components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from "../../components/ui/alert-dialog";
import { Plus, Trash2, Edit, CheckSquare, Calendar, Flag, Download } from "lucide-react";

const priorityStyles = {
  low: "bg-slate-100 text-slate-600 border-slate-200",
  medium: "bg-amber-100 text-amber-700 border-amber-200",
  high: "bg-red-100 text-red-600 border-red-200",
};

const statusStyles = {
  todo: "bg-slate-100 text-slate-600",
  in_progress: "bg-sky-100 text-sky-700",
  done: "bg-emerald-100 text-emerald-700",
};

const emptyForm = { title: "", description: "", assigned_to: "", priority: "medium", due_date: "" };

export default function TaskManagement() {
  const [tasks, setTasks] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [filter, setFilter] = useState("all");
  const [showModal, setShowModal] = useState(false);
  const [editTask, setEditTask] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [deleteId, setDeleteId] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchAll = () => {
    Promise.all([api.get("/tasks"), api.get("/employees")])
      .then(([t, e]) => { setTasks(t.data); setEmployees(e.data); });
  };

  useEffect(() => { fetchAll(); }, []);

  const openCreate = () => { setEditTask(null); setForm(emptyForm); setShowModal(true); };
  const openEdit = (t) => {
    setEditTask(t);
    setForm({ title: t.title, description: t.description || "", assigned_to: t.assigned_to, priority: t.priority, due_date: t.due_date || "" });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.title || !form.assigned_to) { toast.error("Title and assignee are required"); return; }
    setLoading(true);
    try {
      if (editTask) {
        await api.put(`/tasks/${editTask.id}`, form);
        toast.success("Task updated");
      } else {
        await api.post("/tasks", form);
        toast.success("Task created");
      }
      setShowModal(false);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error saving task");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/tasks/${deleteId}`);
      toast.success("Task deleted");
      setDeleteId(null);
      fetchAll();
    } catch { toast.error("Failed to delete"); }
  };

  const handleExport = () => {
    if (!tasks.length) { toast.error("No tasks to export"); return; }
    const toExport = filter === "all" ? tasks : tasks.filter((t) => t.status === filter);
    const sheet = toExport.map((t) => ({
      Title: t.title, Description: t.description || "",
      "Assigned To": t.assigned_to_name, Priority: t.priority,
      "Due Date": t.due_date || "", Status: t.status,
      Created: new Date(t.created_at).toLocaleDateString("en-IN"),
    }));
    exportToExcel([{ data: sheet, name: "Tasks" }], `Tasks_${filter}`);
    toast.success("Tasks exported!");
  };

  const handleStatusChange = async (id, status) => {
    try {
      await api.put(`/tasks/${id}/status`, { status });
      setTasks((prev) => prev.map((t) => (t.id === id ? { ...t, status } : t)));
    } catch { toast.error("Failed to update status"); }
  };

  const tabs = ["all", "todo", "in_progress", "done"];
  const filtered = filter === "all" ? tasks : tasks.filter((t) => t.status === filter);

  return (
    <Layout>
      <div className="p-8 page-enter">
        <div className="flex items-center justify-between mb-7">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Task Management</h1>
            <p className="text-slate-500 text-sm mt-1">{tasks.length} total tasks</p>
          </div>
          <Button onClick={openCreate} className="bg-[#1a2332] hover:bg-[#2c3e50] text-white gap-2" data-testid="create-task-btn">
            <Plus size={16} /> Create Task
          </Button>
        </div>

        {/* Tabs + Export */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex gap-2 bg-slate-100 p-1 rounded-lg">
            {tabs.map((t) => {
              const count = t === "all" ? tasks.length : tasks.filter((x) => x.status === t).length;
              return (
                <button
                  key={t}
                  onClick={() => setFilter(t)}
                  className={`px-4 py-1.5 rounded-md text-sm font-medium capitalize transition-all ${
                    filter === t ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"
                  }`}
                  data-testid={`task-tab-${t}`}
                >
                  {t.replace("_", " ")} <span className="ml-1 text-xs opacity-70">({count})</span>
                </button>
              );
            })}
          </div>
          <Button
            onClick={handleExport}
            variant="outline"
            className="gap-2 border-slate-200 text-slate-700 hover:border-[#38b6e8] hover:text-[#38b6e8]"
            data-testid="export-tasks-btn"
          >
            <Download size={15} /> Export Excel
          </Button>
        </div>

        {/* Task cards */}
        {filtered.length === 0 ? (
          <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
            <CheckSquare size={40} className="text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500 font-medium">No tasks found</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="tasks-grid">
            {filtered.map((task) => (
              <div key={task.id} className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 hover:shadow-md transition-shadow" data-testid={`task-card-${task.id}`}>
                <div className="flex items-start justify-between gap-2 mb-3">
                  <h3 className="font-semibold text-slate-800 text-sm leading-snug">{task.title}</h3>
                  <div className="flex gap-1.5 shrink-0">
                    <button onClick={() => openEdit(task)} className="p-1.5 rounded text-slate-400 hover:text-[#1a2332] hover:bg-slate-100 transition-colors" data-testid={`edit-task-${task.id}`}>
                      <Edit size={13} />
                    </button>
                    <button onClick={() => setDeleteId(task.id)} className="p-1.5 rounded text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors" data-testid={`delete-task-${task.id}`}>
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>
                {task.description && <p className="text-slate-500 text-xs mb-3 line-clamp-2">{task.description}</p>}
                <div className="flex items-center gap-2 flex-wrap mb-4">
                  <Badge className={`${priorityStyles[task.priority]} border text-xs`}>
                    <Flag size={10} className="mr-1" />{task.priority}
                  </Badge>
                  {task.due_date && (
                    <span className="flex items-center gap-1 text-xs text-slate-500">
                      <Calendar size={11} />{task.due_date}
                    </span>
                  )}
                </div>
                <div className="flex items-center justify-between">
                  <p className="text-xs text-slate-500">{task.assigned_to_name}</p>
                  <Select value={task.status} onValueChange={(v) => handleStatusChange(task.id, v)}>
                    <SelectTrigger className={`w-28 h-7 text-xs ${statusStyles[task.status]} border-0 font-semibold`} data-testid={`task-status-${task.id}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="todo">Todo</SelectItem>
                      <SelectItem value="in_progress">In Progress</SelectItem>
                      <SelectItem value="done">Done</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editTask ? "Edit Task" : "Create New Task"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-sm font-medium text-slate-700">Title *</Label>
              <Input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="Task title"
                className="mt-1.5 border-slate-200 focus-visible:ring-[#38b6e8]"
                data-testid="task-title-input"
              />
            </div>
            <div>
              <Label className="text-sm font-medium text-slate-700">Description</Label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Task details..."
                className="mt-1.5 border-slate-200 focus-visible:ring-[#38b6e8] resize-none"
                rows={3}
                data-testid="task-description-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm font-medium text-slate-700">Assign To *</Label>
                <Select value={form.assigned_to} onValueChange={(v) => setForm({ ...form, assigned_to: v })}>
                  <SelectTrigger className="mt-1.5 border-slate-200 focus:ring-[#38b6e8]" data-testid="task-assignee-select">
                    <SelectValue placeholder="Select employee" />
                  </SelectTrigger>
                  <SelectContent>
                    {employees.map((e) => (
                      <SelectItem key={e.id} value={e.id}>{e.first_name} {e.last_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm font-medium text-slate-700">Priority</Label>
                <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                  <SelectTrigger className="mt-1.5 border-slate-200" data-testid="task-priority-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-sm font-medium text-slate-700">Due Date</Label>
              <Input
                type="date"
                value={form.due_date}
                onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                className="mt-1.5 border-slate-200 focus-visible:ring-[#38b6e8]"
                data-testid="task-due-date-input"
              />
            </div>
          </div>
          <DialogFooter className="mt-5">
            <Button variant="outline" onClick={() => setShowModal(false)} className="border-slate-200">Cancel</Button>
            <Button onClick={handleSave} disabled={loading} className="bg-[#1a2332] hover:bg-[#2c3e50] text-white" data-testid="save-task-btn">
              {loading ? "Saving..." : editTask ? "Save Changes" : "Create Task"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Task</AlertDialogTitle>
            <AlertDialogDescription>This will permanently delete this task. Continue?</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-task">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}
